import React, { useMemo } from 'react';
import ReactGA from 'react-ga4';
import Skeleton from '@mui/material/Skeleton';

import { OrganizationPhaseCommitments } from 'api/commitments';

import SegmentedProgressBar from './SegmentedProgressBar';

import MemberComplete from 'assets/member-complete.png';
import LaurelLeft from 'assets/laurel-left.png';
import LaurelRight from 'assets/laurel-right.png';

import Button from '@mui/material/Button';
import { createStyles, makeStyles } from '@mui/styles';
import Routes from 'routes';
import { SignUpAvailabilityResult } from 'util/user';

const useStyles = makeStyles(() =>
  createStyles({
    organizationNameHeading: {
      margin: 0,
      fontSize: '20px',
      fontWeight: 700,
      color: '#1F2051',
      textAlign: 'center',
    },
    enrollmentContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignSelf: 'flex-start',
      gap: '40px',
      margin: 'clamp(-240px, -20%, -60px) 0 20px 24px',
      maxWidth: '640px',
      minWidth: '500px',
      width: '50%',
      '@media (max-width: 820px)': {
        alignSelf: 'center',
        minWidth: '300px',
        width: '90%',
        margin: 'clamp(-100px, -25%, -40px) auto 24px',
      },
    },
    enrollmentProgressContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '20px',
      background: '#FFFFFF',
      borderRadius: '10px',
      boxShadow: '0px 8px 16px 4px rgba(0, 0, 0, 0.05)',
      padding: '24px 24px',
    },
    enrolledHeadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '48px',
      marginBottom: '-10px',
      '& img': {
          height: '80px !important',
      },
      '@media (max-width: 820px)': {
        gap: '24px',
        '& img': {
          height: '64px !important',
        }
      },
    },
    completedHeadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#1F2051',
      fontWeight: 700,
      fontSize: '28px',
      gap: '12px',
      marginBottom: '-10px',
      '& img': {
          height: '160px !important',
      },
      '@media (max-width: 820px)': {
        '& img': {
          height: '120px !important',
        }
      },
    },
    welcomeText: {
      fontSize: '36px',
      fontWeight: 800,
      textAlign: 'center',
    },
    enrolledCount: {
      fontSize: '44px',
      fontWeight: 800,
    },
    enrolledHeading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#1F2051',
      width: '100%',
    },
    enrolledMax: {
      fontSize: '24px',
      fontWeight: 400,
    },
    enrolledDescription: {
      fontSize: '20px',
      fontWeight: 400,
      marginTop: '-6px',
      textAlign: 'center',
    },
    faqContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#7378E8',
      fontSize: '14px',
    },
    goal: {
      alignSelf: 'flex-start',
      fontSize: '24px',
      margin: '8px 0',
      fontWeight: 400,
      '& b': {
        fontWeight: 800,
      },
    },
    headingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '24px',
      width: '100%',
    },
    memberContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    name: {
      color: '#FFFFFF',
      fontSize: '36px',
      fontWeight: 700,
      lineHeight: '54px',
      textAlign: 'center',
    },
    subheading: {
      color: '#5A6A81',
      fontWeight: 300,
      textAlign: 'center',
    },
    signupBtn: {
      /* base layout */
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: '14px 32px 14px 20px',
    
      /* look & feel */
      background: 'rgba(153, 157, 255, 1) !important',   // brand colour (never re-declared)
      color: '#FFFFFF',
      border: 'none',
      borderRadius: 8,
      fontSize: 18,
      fontWeight: 700,
      textTransform: 'none',
      cursor: 'pointer',
    
      /* motion */
      transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    
      /* touch-webkit quirk */
      '-webkit-tap-highlight-color': 'transparent',
    
      /* hover (lift) */
      '&:hover': {
        transform: 'scale(1.02)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      },
    
      /* active / pressed (dip) */
      '&:active': {
        transform: 'scale(0.97)',
        boxShadow: '0 2px 6px rgba(0,0,0,0.20)',
      },
    
      /* keyboard focus outline */
      '&:focus-visible': {
        outline: '1px solid rgba(153, 157, 255, 0.6)',
        outlineOffset: 5,
      },

      /* disabled state */
      '&.Mui-disabled': {
        opacity: 0.4,
        pointerEvents: 'none',
        color: '#FFFFFF !important',
        '& .MuiButton-endIcon': {
          color: '#FFFFFF !important'
        }
      }
    },
    signupBtnDisabled: {
      fontSize: '14px',
      fontWeight: 300,
      color: '#A6A6BF',
      opacity: 1,
      pointerEvents: 'none',
      textAlign: 'left',
      display: 'block',
      width: '100%',
    },
  })
);

type EnrollmentProgressProps = {
  setEligibilityPopUp: (open: boolean) => void;
  isLoading: boolean;
  activeCommitments?: OrganizationPhaseCommitments;
  canSignUpMember: SignUpAvailabilityResult;
};

const EnrollmentProgress: React.FC<EnrollmentProgressProps> = ({
  setEligibilityPopUp,
  isLoading,
  activeCommitments,
  canSignUpMember
}) => {
  const classes = useStyles();

  const organizationName = useMemo(() => 
    activeCommitments?.organization?.name || '', 
    [activeCommitments?.organization?.name]
  );
  
  const { seats_filled, seat_goal } = useMemo(() => ({
    seats_filled: activeCommitments?.commitments?.reduce((total: number, commitment: { seats_used_actual_in_phase: number }) => 
      total + commitment.seats_used_actual_in_phase, 0) || 0,
    seat_goal: activeCommitments?.commitments?.reduce((total: number, commitment: { seat_total: number }) => 
      total + commitment.seat_total, 0) || 0
  }), [activeCommitments?.commitments]);

  const signedUp = useMemo(() => 
    activeCommitments?.user_signed_up || 0,
    [activeCommitments?.user_signed_up]
  );

  const isWelcome = seats_filled === 0;
  const isFull = useMemo(() => 
    activeCommitments?.commitments?.every((commitment: { seats_used_actual_in_phase: number, seat_total: number }) => 
      commitment.seats_used_actual_in_phase >= commitment.seat_total) ?? false,
    [activeCommitments?.commitments]
  );

  const handleEligibilityCheckClick = () => {
    ReactGA.event({
      category: 'Navigation',
      action: 'Click',
      label: 'Check Member Eligibility Button',
    });

    setEligibilityPopUp(true);
  };

  const renderHeading = () => {
    if (isWelcome) {
      return (
        <>
          <div>
            <span className={classes.welcomeText}>
              <b>Welcome</b>
            </span>
          </div>
          <span className={classes.enrolledDescription}>
            to Samaritan
          </span>
        </>
      );
    }
    
    return (
      <>
        <div>
          <span className={classes.enrolledCount}>
            <b>{seats_filled}</b>
          </span>
          <span className={classes.enrolledMax}> / {seat_goal}</span>
        </div>
        <span className={classes.enrolledDescription}>
          Members Enrolled
        </span>
      </>
    );
  };

  const renderSubHeading = () => {
    return (
      <span className={classes.subheading}>
        {signedUp === 0 ? (
          "Let's get your first member started today!"
        ) : (
          <div className={classes.memberContainer}>
            <span>
              You&apos;ve signed up{' '}
              <span style={{ fontSize: '18px', fontWeight: 700 }}>
                {signedUp}
              </span>{' '}
              — great work! 👏
            </span>
          </div>
        )}
      </span>
    );
  };

  const renderEnrolledHeading = () => {
    return (
      <div className={classes.enrolledHeadingContainer}>
        <img src={LaurelLeft} alt="laurel left" height={0} />
        <div className={classes.enrolledHeading}>{renderHeading()}</div>
        <img src={LaurelRight} alt="laurel right" height={0} />
      </div>
    );
  };

  const renderCompletedHeading = () => {
    return (
      <div className={classes.completedHeadingContainer}>
        <img
          alt="member complete"
          src={MemberComplete}
          style={{ margin: 'auto' }}
        />
        <span>
          All {seat_goal} Members Enrolled!
        </span>
      </div>
    );
  };

  const renderGoal = () => {
    // If the goal is full, don't render the goal
    if (isFull) return null;
    // If the goal is not full, render the goal
    if (seat_goal > seats_filled) 
      return (
      <>
        <div className={classes.goal}>
          Goal: <b>{seat_goal - seats_filled}</b><span style={{ fontSize: '20px'}}>
              {" Members Left to Go"}
            </span>
        </div>
      </>
    );
    // If the goal is not full, render the goal for each commitment
    const commitmentsWithSeats = activeCommitments?.commitments?.filter(commitment => 
      commitment.seat_total - commitment.seats_used_actual_in_phase > 0
    );
    
    if (!commitmentsWithSeats?.length) return null;
    
    const totalSeatsRemaining = commitmentsWithSeats.reduce((total, commitment) => 
      total + (commitment.seat_total - commitment.seats_used_actual_in_phase), 0
    );
    
    const deploymentNames = commitmentsWithSeats.map((commitment, index) => 
      commitment.deployment_name || `Commitment ${String.fromCharCode(65 + index)}`
    ).join(', ');
    
    return (
      <div className={classes.goal}>
        Goal: <b>{totalSeatsRemaining}</b><span style={{ fontSize: '20px'}}>
            {" Members Left to Go"}{commitmentsWithSeats.length === 1 ? ` on ${deploymentNames}` : ''}
          </span>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={classes.enrollmentContainer}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={550}
          animation="wave"
          sx={{
            borderRadius: '10px',
            bgcolor: '#e0e0e0'
          }}
        />
      </div>
    );
  }

  return (
    <div className={classes.enrollmentContainer}>
      <div className={classes.enrollmentProgressContainer}>
        <div className={classes.headingContainer}>
          <h2 className={classes.organizationNameHeading}>
            {organizationName} Enrollment Progress
          </h2>

          <div className={classes.enrolledHeadingContainer}>
            { isFull ? renderCompletedHeading() : renderEnrolledHeading() }
          </div>

          {renderSubHeading()}
        </div>

        <SegmentedProgressBar commitments={activeCommitments} />

        {renderGoal()}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
          <Button
            className={classes.signupBtn}
            disableElevation
            endIcon={<i className="fas fa-arrow-right" />}
            onClick={
              handleEligibilityCheckClick
            }
            variant="contained"
            disabled={!canSignUpMember.canSignUp}
            aria-label={
              !canSignUpMember.canSignUp && canSignUpMember.errorMessage
                ? `Sign Up a New Member Button Disabled: ${canSignUpMember.errorMessage?.longReason}`
                : "Sign Up a New Member Button and Navigate to Member Registration"
            }
          >
            Sign Up a New Member
          </Button>
          {!canSignUpMember.canSignUp && (
            <span className={classes.signupBtnDisabled}>{canSignUpMember.errorMessage?.longReason}</span>
          )}
        </div>

        {isWelcome && (
          <a
            href={Routes.wiki}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: '#7378E8' }}
          >
            <div className={classes.faqContainer}>
              <i className="fas fa-question" style={{
                border: '1.5px solid #7378E8',
                borderRadius: '50%',
                fontSize: '10px',
                boxSizing: 'content-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
              }} />
              Need help? Check out our FAQ
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

export default EnrollmentProgress;
